import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { AccessPermission } from "@entrilia/shared/api/types.ts";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type { ReportInfo } from "../../../../../../api/types/svTypes.ts";
import { ReportType } from "../../../../../../api/types/svTypes.ts";
import { chartRelatedReportTypes } from "../../../sv/utilities/chartRelatedReport.types.ts";

const copyableReportTypes = [
  ReportType.Pivot,
  ReportType.Tabular,
  ReportType.List,
  ReportType.CashFlow,
  ...chartRelatedReportTypes,
  ReportType.Tile,
];

export type BulkActionReport = Pick<ReportInfo, "reportId" | "clientCode" | "authorization">;

export interface BulkActionState<T extends BulkActionReport = BulkActionReport> {
  availableReports: T[];
  buttonText: string;
  tooltipText?: string;
}

export const getBulkActionState = <T extends BulkActionReport>(
  selectedReports: T[],
  isAvailable: (report: T) => boolean,
  actionLabel: string,
  partialAccessTooltip: string
): BulkActionState<T> => {
  const availableReports = selectedReports.filter(isAvailable);
  const allAvailable = availableReports.length === selectedReports.length;
  return {
    availableReports,
    buttonText: allAvailable ? actionLabel : `${actionLabel} (${availableReports.length})`,
    tooltipText: allAvailable ? undefined : partialAccessTooltip,
  };
};

export const canMoveReport = (
  report: BulkActionReport,
  hasAnyPermissionForClient: (clientCode: string, permissions: AccessPermission[]) => boolean
): boolean => hasAnyPermissionForClient(report.clientCode, ["ManageAllReports"]);

export const canShareReport = (
  report: BulkActionReport,
  hasAnyPermissionForClient: (clientCode: string, permissions: AccessPermission[]) => boolean
): boolean =>
  hasAnyPermissionForClient(report.clientCode, ["ShareReports"]) &&
  checkAccess(report.authorization.access, ReportAccessType.FullAccess);

export const canCopyReport = (report: Partial<Pick<ReportInfo, "reportType">>): boolean =>
  report.reportType !== undefined && copyableReportTypes.includes(report.reportType);
